import { Injectable, NgZone, inject } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import {
  ActivatedRouteSnapshot,
  LoadChildren,
  Route,
  Router,
  RouterStateSnapshot,
  Routes,
  UrlTree
} from '@angular/router';
import {
  SharedPopupService,
  getThemeAwareColor as canonicalGetThemeAwareColor,
  getAccessibleTextColor as canonicalGetAccessibleTextColor,
  getAccessibleButtonColor as canonicalGetAccessibleButtonColor
} from '@bepatient/shared';
import { Capacitor } from '@capacitor/core';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { FileOpener } from '@capacitor-community/file-opener';
import { Filesystem, Directory, WriteFileResult } from '@capacitor/filesystem';
import { Browser } from '@capacitor/browser';
import { Network } from '@capacitor/network';
import { PushNotifications, Token } from '@capacitor/push-notifications';
import { Preferences } from '@capacitor/preferences';
import { Platform, ToastController } from '@ionic/angular';
import { Action, MemoizedSelector, select, Store } from '@ngrx/store';
// import { MyFormActionSaveCurrentPage } from '@bepatient/my-form-store'; // Commented out to avoid circular dependency
import { TranslateService } from '@ngx-translate/core';
import * as CryptoJS from 'crypto-js';
import { iif, Observable, of, forkJoin, BehaviorSubject, Subject } from 'rxjs';
import {
  filter,
  map,
  switchMap,
  take,
  withLatestFrom,
  catchError
} from 'rxjs/operators';
import {
  CorePageModule,
  CoreLogin,
  ENVIRONMENT,
  Environment
} from './core.models';
import { CoreExternalAppService } from './core-external-app.service';
import { BiometricService } from './biometric.service';
import {
  selectorCoreInstanceInfo,
  selectorCoreLogin,
  selectorCorePreviousAttemptingUrl
} from './core.reducer';
import { format, parseISO } from 'date-fns';
import {
  CoreActionSaveDeeplinkFormData,
  CoreActionSetFCMToken,
  CoreActionSetNotificationTap,
  CoreActionSetAppActiveStatus
} from './core.actions';
// import { environment } from 'apps/smart-app/src/environments/environment';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import chroma from 'chroma-js';

export class CoreUtils {
  static DEPTH_MODULE_MAX_DEPTH = 16;

  static generateDepthModuleRoutes(
    path: string,
    loadChildren: LoadChildren,
    canActivateGuard: any
  ): Routes {
    return new Array<Route>(CoreUtils.DEPTH_MODULE_MAX_DEPTH)
      .fill(void 0)
      .map((e, i) => ({
        path: `${path}-depth-${i}`,
        canActivate: [canActivateGuard],
        loadChildren
      }));
  }
}

export abstract class CoreSelectorGuard {
  protected constructor(
    private store: Store<any>,
    private selector?: MemoizedSelector<object, any>,
    private actionToDispatch?: Action,
    private skipForPreviousAttemptingUrl?: string | string[],
    private domSanitizer?: DomSanitizer
  ) {
    if (!selector) {
      console.error(
        'CoreSelectorGuard: Selector is undefined - guard will return false'
      );
    } else if (typeof selector !== 'function') {
      console.error(
        'CoreSelectorGuard: Selector is not a function - guard will return false',
        selector
      );
    }
  }

  safeUrl: SafeUrl;

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.store.pipe(
      withLatestFrom(
        this.store.select(selectorCoreLogin),
        this.store.select(selectorCorePreviousAttemptingUrl)
      ),
      take(1),
      switchMap(([, login, url]) => {
        if (login?.iAuth) {
          if (
            this.skipForPreviousAttemptingUrl &&
            (this.skipForPreviousAttemptingUrl === url ||
              (Array.isArray(this.skipForPreviousAttemptingUrl) &&
                this.skipForPreviousAttemptingUrl.includes(url)))
          ) {
            return of(true);
          }
          if (this.actionToDispatch) {
            this.store.dispatch(this.actionToDispatch);
          }
          // Check selector validity before using it
          if (!this.selector || typeof this.selector !== 'function') {
            console.error(
              'CoreSelectorGuard: Selector is invalid, cannot proceed with guard',
              {
                selector: this.selector,
                selectorType: typeof this.selector
              }
            );
            return of(false);
          }

          try {
            return this.store.select(this.selector).pipe(
              filter(selection => !!selection),
              take(1),
              map(() => true),
              catchError(error => {
                console.error(
                  'CoreSelectorGuard: Error selecting from store:',
                  error
                );
                return of(false);
              })
            );
          } catch (error) {
            console.error(
              'CoreSelectorGuard: Exception in store.select:',
              error
            );
            return of(false);
          }
        } else {
          return of(false);
        }
      })
    );
  }
}

export abstract class CoreLoginGuard {
  constructor(private store: Store, private router: Router) {}

  /**
   * This guard controls access to a route based on user login status.
   * If a user is logged in, it either redirects to the previous URL
   * where login was attempted or to the timeline page. If the user
   * is not logged in, access to the child route is prevented.
   *
   * @param route The activated route snapshot
   * @param state The router state snapshot
   * @returns An observable emitting a boolean or UrlTree
   */

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    // Fetch the previous URL where login was attempted
    return this.store.select(selectorCorePreviousAttemptingUrl).pipe(
      take(1), // Take only the first emitted value, then complete the observable
      switchMap(previousUrl => {
        // Get user login status
        return this.store.select(selectorCoreLogin).pipe(
          map(login => {
            if (
              login &&
              login.iAuth &&
              !login.userIsInOnboarding &&
              !login.userMustEnterOnboarding
            ) {
              // User is logged in, redirect to previous url or timeline
              return this.router.parseUrl(
                previousUrl ? previousUrl : '/timeline'
              );
            } else {
              // User is not logged in, prevent access to route
              return true;
            }
          })
        );
      })
    );
  }
}

@Injectable()
export class CoreUtilsService {
  private router = inject(Router);
  private store$ = inject<Store<any>>(Store);
  private translate = inject(TranslateService);
  private sharedPopupService = inject(SharedPopupService);
  private platform = inject(Platform);
  private toastCtrl = inject(ToastController);
  private ngZone = inject(NgZone);
  private coreExternalAppService = inject(CoreExternalAppService);
  private biometric = inject(BiometricService);
  private env = inject<Environment>(ENVIRONMENT);

  // private appIsOnline$: Observable<boolean> = undefined;

  /**
   * @let integer Return encrypt method or Cipher method number. (128, 192, 256)
   */
  get encryptMethodLength() {
    const encryptMethod = this.encryptMethod;
    // get only number from string.
    // @link https://stackoverflow.com/a/10003709/128761 Reference.
    const aesNumber = encryptMethod.match(/\d+/)[0];
    return parseInt(aesNumber, 10);
  }

  /**
   * @let integer Return cipher method divide by 8. example: AES number 256 will be 256/8 = 32.
   */
  get encryptKeySize() {
    const aesNumber = this.encryptMethodLength;
    return aesNumber / 8;
  }

  /**
   * @link http://php.net/manual/en/function.openssl-get-cipher-methods.php
   *       Refer to available methods in PHP if we are working between JS & PHP encryption.
   * @let string Cipher method.
   *       Recommended AES-128-CBC, AES-192-CBC, AES-256-CBC
   *       due to there is no `openssl_cipher_iv_length()` function in JavaScript
   *       and all of these methods are known as 16 in iv_length.
   */
  get encryptMethod(): string {
    return 'AES-256-CBC';
  }

  networkConnection = new BehaviorSubject<boolean>(true);
  appStateActive: Subject<boolean> = new BehaviorSubject(true);

  constructor() {
    // Set the network connection status on initial app load
    Network.getStatus().then(network => {
      this.ngZone.run(() => {
        this.networkConnection.next(network.connected);
      });
    });

    // Update network connection status on network change
    Network.addListener('networkStatusChange', status => {
      this.ngZone.run(() => {
        this.networkConnection.next(status.connected);
      });
    });

    App.addListener('appStateChange', state => {
      this.ngZone.run(() => {
        this.store$.dispatch(new CoreActionSetAppActiveStatus(state?.isActive));
        this.appStateActive.next(state?.isActive);

        // If app is becoming active, do NOTHING - no auto-processing at all
        if (state?.isActive) {
          // App became active - no action needed
        }
      });
    });
  }

  addDepthModuleRoutes(module: CorePageModule): void {
    module.routes.forEach(route => this.router.config.push(route));
  }

  openExternalUrlInBrowser(url: string): void {
    if (Capacitor.isNativePlatform()) {
      Browser.open({ url });
    } else {
      window.open(url, '_blank');
    }
  }

  isValidJson(json: any): boolean {
    try {
      JSON.parse(json);
    } catch (e) {
      return false;
    }
    return true;
  }

  navigateToExternalUrl(url: string): void {
    const expression =
      '/(https?://(?:www.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9].[^s]{2,}|www.[a-zA-Z0-9][a-zA-Z0-9-]' +
      '+[a-zA-Z0-9].[^s]{2,}|https?://(?:www.|(?!www))[a-zA-Z0-9]+.[^s]{2,}|www.[a-zA-Z0-9]+.[^s]{2,})/gi';
    const LINK_REGEXP = new RegExp(expression);
    const allowedLinks = [
      '/app-store/',
      'play.google.com/store/apps',
      'mailto',
      'tel'
    ];
    const res = allowedLinks.filter(e => url.includes(e));

    // InApp link  handle case , kept for future improvements
    // if (this.IsValidJson(url)) {
    //    const data = JSON.parse(url);
    //
    //    this.ClickEvent(data.key, data.content,data.link);
    //  }

    if (res.pop()) {
      this.openExternalUrlInBrowser(url);
    } else {
      forkJoin([
        this.getSafeTranslations([
          'OPEN_LINK.TITLE',
          'OPEN_LINK.MESSAGE',
          'OPEN_LINK.NO',
          'OPEN_LINK.YES'
        ]),
        this.store$.pipe(select(selectorCoreInstanceInfo), take(1))
      ]).subscribe(([translations, instanceInfo]) => {
        this.sharedPopupService
          .simpleAlert({
            id: 'Navigate to External Url',
            color: instanceInfo && instanceInfo.appColor,
            title: translations[0],
            message: translations[1],
            buttons: [translations[2], translations[3]],
            alertClass: 'bepatient-outapp-alert'
          })
          .then(index => {
            if (index === 1) {
              this.openExternalUrlInBrowser(url);
            }
          });
      });
    }
  }

  /**
   * Because sometimes the translations are not loaded, we make sure here that the TranslateService does not return the keys.
   *
   * Beware, if the key and the translation have the same value, we enter into an infinite loop.
   * (for example if the 'OK' key is translated to 'OK')
   *
   * @param keys An array of the keys
   * @param takeOne Avoids unsubscribing (true by default)
   *
   * @returns An array of the translations (e.g. for ['KEY_A', 'KEY_B'] => ['TRANSLATION_A', 'TRANSLATION_B'])
   */
  getSafeTranslations(keys: string[], takeOne = true): Observable<string[]> {
    const translations$ = this.translate.stream(keys).pipe(
      filter(stream =>
        keys.every(key => stream[key] !== undefined && stream[key] !== key)
      ),
      map(stream => keys.map(key => stream[key]))
    );

    return iif(() => takeOne, translations$.pipe(take(1)), translations$);
  }

  /**
   * Decrypt string.
   *
   * @link https://stackoverflow.com/questions/41222162/encrypt-in-php-openssl-and-decrypt-in-javascript-cryptojs Reference.
   * @link https://stackoverflow.com/questions/25492179/decode-a-base64-string-using-cryptojs Crypto JS base64 encode/decode reference.
   * @param {string} encryptedString The encrypted string to be decrypt.
   * @param {string} key The key.
   * @return string Return decrypted string.
   */

  decrypt(encryptedString: string, key: string) {
    const json = JSON.parse(
      CryptoJS.enc.Utf8.stringify(CryptoJS.enc.Base64.parse(encryptedString))
    );
    const salt = CryptoJS.enc.Hex.parse(json.salt);
    const iv = CryptoJS.enc.Hex.parse(json.iv);

    const encrypted = json.ciphertext; // no need to base64 decode.
    let iterations = parseInt(json.iterations, 10);
    if (iterations <= 0) {
      iterations = 999;
    }
    // key = Forge.util.decode64(key);
    // Decode base64
    key = atob(key);
    const encryptMethodLength = this.encryptMethodLength / 4; // example: AES number is 256 / 4 = 64
    const hashKey = CryptoJS.PBKDF2(key, salt, {
      hasher: CryptoJS.algo.SHA512,
      keySize: encryptMethodLength / 8,
      iterations: iterations
    });

    const decrypted = CryptoJS.AES.decrypt(encrypted, hashKey, {
      mode: CryptoJS.mode.CBC,
      iv: iv
    });

    return decrypted.toString(CryptoJS.enc.Utf8);
  }

  /**
   * Encrypt string.
   *
   * @link https://stackoverflow.com/questions/41222162/encrypt-in-php-openssl-and-decrypt-in-javascript-cryptojs Reference.
   * @link https://stackoverflow.com/questions/25492179/decode-a-base64-string-using-cryptojs Crypto JS base64 encode/decode reference.
   * @param string The original string to be encrypt.
   * @param key The key used to encrypt.
   * @return encrypted string.
   */
  encrypt(stringToEncrypt, key: string = null) {
    const iv = CryptoJS.lib.WordArray.random(16); // the reason to be 16, please read on `encryptMethod` property.

    const salt = CryptoJS.lib.WordArray.random(256);
    const iterations = 999;
    key = this.generateRandomString(22);
    const encryptMethodLength = this.encryptMethodLength / 4; // example: AES number is 256 / 4 = 64
    const hashKey = CryptoJS.PBKDF2(key, salt, {
      hasher: CryptoJS.algo.SHA512,
      keySize: encryptMethodLength / 8,
      iterations: iterations
    });

    const encrypted = CryptoJS.AES.encrypt(stringToEncrypt, hashKey, {
      mode: CryptoJS.mode.CBC,
      iv: iv
    });
    const encryptedString = CryptoJS.enc.Base64.stringify(encrypted.ciphertext);

    const output = {
      ciphertext: encryptedString,
      iv: CryptoJS.enc.Hex.stringify(iv),
      salt: CryptoJS.enc.Hex.stringify(salt),
      iterations: iterations
    };
    return {
      payload: CryptoJS.enc.Base64.stringify(
        CryptoJS.enc.Utf8.parse(JSON.stringify(output))
      ),
      signature: btoa(key)
    };
  }

  /**
   * @param length length of random string
   * @returns string
   */
  generateRandomString(length: number = 16): string {
    let result = '';
    const characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

  /**
   * Document parser
   * * Check platform hybrid or not
   * * Parse received content using [convertBase64ToBlob]{@link #convertBase64ToBlob} to Blob object
   *
   * * Write file in temporary directory and open using any file opener for hybrid app
   * * For web view open document in new window
   *
   * @param data, document info with status code
   *
   * @default file_name: 'Document.pdf'
   * @default file_type: 'application/pdf'
   *
   * @example payload: { content: base64, file_name?: string, file_type?: string } from API response
   *
   * @returns
   * */
  documentParser(data: any): void {
    if (data && data.payload && data.payload.content) {
      data = data.payload;
      const fileName = data.file_name
        ? data.file_name.replace(/\//g, '_')
        : 'Document.pdf';
      const contentType = data.file_type
        ? `application/${data.file_type}`
        : data.file_name
        ? `application/${data.file_name.split('.')[1]}`
        : 'application/pdf';

      if (this.platform.is('hybrid')) {
        if (Filesystem) {
          Filesystem.writeFile({
            path: fileName,
            directory: Directory.Data,
            data: data.content
          })
            .then((result: WriteFileResult) => {
              FileOpener.open({
                filePath: result.uri,
                contentType
              }).catch(e => {
                this.presentToaster(
                  this.translate.instant('MY_ACCOUNT_DOCUMENT.TOAST.ERROR')
                );
              });
            })
            .catch(e => {
              this.presentToaster(
                this.translate.instant('MY_ACCOUNT_DOCUMENT.TOAST.ERROR')
              );
            });
        }
      } else {
        setTimeout(() => {
          const objBlob = this.convertBase64ToBlob(data.content, contentType);
          const objectURL = URL.createObjectURL(objBlob);
          const w = window.open(
            objectURL,
            '',
            'toolbar=no,titlebar=no,scrollbars=no,resizable=yes,top=500,left=500,width=500,height=700,location=no'
          );
          if (!w || w.closed || typeof w.closed == 'undefined') {
            //POPUP BLOCKED
            this.presentToaster(
              this.translate.instant('MY_ACCOUNT_DOCUMENT.TOAST.POPUP_BLOCKED')
            );
          } else {
            this.presentToaster(
              this.translate.instant('MY_ACCOUNT_DOCUMENT.TOAST.SUCCESS')
            );
          }
        }, 1000);
      }
    } else {
      this.presentToaster(
        this.translate.instant('MY_ACCOUNT_DOCUMENT.TOAST.FETCH_ERROR')
      );
    }
  }

  /**
   * Convert Base64 to Blob
   * @param b64Data, base64 string to convert
   * @param contentType, string which has common MIME type
   *
   * @returns Blob
   */
  convertBase64ToBlob(b64Data: string, contentType: string): Blob {
    contentType = contentType || '';
    const sliceSize = 512;
    b64Data = b64Data.replace(/^[^,]+,/, '');
    b64Data = b64Data.replace(/\s/g, '');
    const byteCharacters = window.atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, { type: contentType });
  }

  /**
   * Get thumbnail from video id (For only Vimeo)
   * @param id supported video url id
   *
   * @returns Obj {thumbnail, thumbnail2x}
   *  thumbnail // low quality image
   *  thumbnail2x // high quality image
   */
  getVideoThumbnail(id: string): Promise<any> {
    return new Promise<any>(resolve => {
      let thumbnail: string;
      let thumbnailLarge: string;
      var xhr = new XMLHttpRequest();
      xhr.open('GET', 'https://vimeo.com/api/v2/video/' + id + '.json', true);
      xhr.onload = e => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            var data = xhr.responseText;
            var parsedData = JSON.parse(data);
            thumbnail = parsedData[0].thumbnail_large;
            // split url of large thumbnail at 640
            // add 1280x720 to parts and get bigger thumbnail
            thumbnailLarge = thumbnail.split(/(\d+)(?!.*\d)/)[0] + '1280x720';
            resolve({
              thumbnail: thumbnail,
              thumbnail2x: thumbnailLarge
            });
          } else {
            console.error(xhr.status, xhr.statusText);
          }
        }
      };
      xhr.onerror = e => {
        console.error(xhr.status, xhr.statusText);
      };
      xhr.send(null);
    });
  }

  /**
   * Method to display toaster
   * @param message
   * @param duration Duration in milliseconds (default: 2000)
   *
   * @returns <promise>, Present the Toaster
   */
  async presentToaster(
    message: string,
    duration: number = 2000
  ): Promise<void> {
    try {
      const toast = await this.toastCtrl.create({
        message: message,
        animated: true,
        position: 'top',
        duration: duration,
        cssClass: 'bepatient_dynamic_form_save_toast'
      });
      await toast.present();
    } catch (error) {
      console.error('Error presenting toast:', error);
    }
  }

  /**
   * Get file mime type and extension from file name
   * @param fileName: string
   * @returns `{ fileMimeType, fileExtension }`
   */
  getFileMimeTypeAndExt(fileName: string): {
    fileMimeType: string;
    fileExtension: string;
  } {
    // const fileExtension = fileName.split('.').pop();
    let filenameextension = fileName.replace(/^.*[\\\/]/, '');
    let ext = filenameextension.split('.').pop();
    let fileMimeType = void 0;
    switch (ext) {
      case 'pdf':
        fileMimeType = 'application/pdf';
        break;
      case 'doc':
        fileMimeType = 'application/msword';
        break;
      case 'docx':
        fileMimeType =
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        break;
      case 'xls':
        fileMimeType = 'application/vnd.ms-excel';
        break;
      case 'xlsx':
        fileMimeType =
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        break;
      case 'png':
        fileMimeType = 'image/png';
        break;
      case 'jpg':
        fileMimeType = 'image/jpeg';
        break;
      case 'jpeg':
        fileMimeType = 'image/jpeg';
        break;
    }
    return {
      fileMimeType,
      fileExtension: ext
    };
  }

  /*
   * On select datetime value parse to show in Input and also set to datetime picker
   * @param formatToParse : datetime format to parse 'dd/mm/yyyy', 'yyyy/mm/dd', 'yyyy/mm' ....
   * @param pickerDate : ISO Format date to convert,
   * @returns If date is not provided, returns user format for display and iso format for datetime picker,
   * Else if date is provided, returns date formatted for display and iso format for datetime picker
   *
   */
  getValidDateFormat(
    formatToParse: string,
    pickerDate?: string
  ): { userFormat: string; isoFormat: string; pickerType: string } {
    let formatToConvert = formatToParse.toLowerCase();
    let userFormat;
    let isoFormat;
    let pickerType;

    switch (formatToConvert) {
      case 'dd/mm/yyyy':
      case 'dd-mm-yyyy':
        userFormat = 'dd/MM/yyyy';
        isoFormat = 'yyyy-MM-dd';
        pickerType = 'date';
        break;
      case 'yyyy/mm/dd':
      case 'yyyy-mm-dd':
        userFormat = 'yyyy/MM/dd';
        isoFormat = 'yyyy-MM-dd';
        pickerType = 'date';
        break;
      case 'yyyy/mm':
      case 'yyyy-mm':
        userFormat = 'yyyy/MM';
        isoFormat = 'yyyy-MM';
        pickerType = 'month-year';
        break;
      case 'mm/yyyy':
      case 'mm-yyyy':
        userFormat = 'MM/yyyy';
        isoFormat = 'yyyy-MM';
        pickerType = 'month-year';
        break;
      case 'yyyy':
        userFormat = 'yyyy';
        isoFormat = 'yyyy';
        pickerType = 'year';
        break;
      case 'yyyy-mm-ddthh:mm:ss':
      case 'yyyy/mm/ddthh:mm:ss':
        userFormat = "yyyy/MM/dd'T'HH:mm:ssxxx";
        isoFormat = "yyyy-MM-dd'T'HH:mm:ssxxx";
        pickerType = 'date-time';
        break;
      case 'hh:mm':
        userFormat = 'hh:mm a'; // display 12h with AM/PM
        isoFormat = 'HH:mm'; // normalize to 24h
        pickerType = 'time';
        break;
      case 'HH:mm':
        userFormat = 'HH:mm'; // display 24h
        isoFormat = 'HH:mm'; // 24h internally
        pickerType = 'time';
        break;
      default:
        userFormat = 'dd/MM/yyyy';
        isoFormat = 'yyyy-MM-dd';
        pickerType = 'date';
    }

    if (pickerDate) {
      if (pickerType === 'time') {
        // Accept both 24h (HH:mm) and 12h (hh:mm AM/PM) inputs and normalize
        const m24 = /^([01]\d|2[0-3]):([0-5]\d)$/;
        const m12 = /^(0?[1-9]|1[0-2]):([0-5]\d)\s?(AM|PM)$/i;

        const to24h = (h: number, m: number, mer: string): string => {
          let H = h % 12;
          if (/pm/i.test(mer)) H += 12;
          if (/am/i.test(mer) && H === 12) H = 0; // 12:xx AM => 00:xx
          const HH = H.toString().padStart(2, '0');
          const MM = m.toString().padStart(2, '0');
          return `${HH}:${MM}`;
        };
        const to12h = (H: number, M: number): string => {
          const mer = H >= 12 ? 'PM' : 'AM';
          let h = H % 12;
          if (h === 0) h = 12;
          const hh = h.toString().padStart(2, '0');
          const mm = M.toString().padStart(2, '0');
          return `${hh}:${mm} ${mer}`;
        };

        if (m24.test(pickerDate)) {
          const [, HHs, MMs] = pickerDate.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
          const HH = parseInt(HHs, 10);
          const MM = parseInt(MMs, 10);
          isoFormat = `${HHs}:${MMs}`; // 24h stays as-is for picker
          // Display depends on desired userFormat
          if (userFormat === 'hh:mm a') {
            userFormat = to12h(HH, MM);
          } else {
            userFormat = `${HHs}:${MMs}`;
          }
        } else if (m12.test(pickerDate)) {
          const [, hs, ms, mer] = pickerDate.match(
            /^(0?[1-9]|1[0-2]):([0-5]\d)\s?(AM|PM)$/i
          );
          const h = parseInt(hs, 10);
          const m = parseInt(ms, 10);
          const HHmm = to24h(h, m, mer);
          isoFormat = HHmm; // picker uses 24h
          // Display depends on desired userFormat
          if (userFormat === 'hh:mm a') {
            userFormat = `${hs
              .toString()
              .padStart(2, '0')}:${ms} ${mer.toUpperCase()}`;
          } else {
            userFormat = HHmm;
          }
        }
      } else {
        // For date/datetime formats, use the standard format conversion
        userFormat = format(parseISO(pickerDate), userFormat);
        isoFormat = format(parseISO(pickerDate), isoFormat);
      }
    }

    return {
      userFormat: userFormat,
      isoFormat: isoFormat,
      pickerType: pickerType
    };
  }

  isValidDateTimeOrTimeString(value, type): Boolean {
    switch (type) {
      case 'datetime':
        const dateTimeRegex =
          /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{1,3})?(Z|[-+]\d{2}:\d{2})?)?$/;
        return dateTimeRegex.test(value);

      case '24h-time':
        const timeRegex = /^(?:2[0-3]|[01][0-9]):[0-5][0-9]$/;
        return timeRegex.test(value);

      case '12h-time':
        const time12Regex = /^(0?[1-9]|1[0-2]):[0-5][0-9]\s?(AM|PM)$/i;
        return time12Regex.test(value);

      default:
        return false;
    }
  }

  /**
   * Check Push Notification Permission, Request Permission,
   * Register Push notification and get the FCM token
   * Permission Request needed only in iOS
   */
  registerPush = async () => {
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }

    // TODO-CAP-MIGRATION: If permission not granted condition
    if (permStatus.receive !== 'granted') {
      throw new Error('User denied permissions!');
    }

    await PushNotifications.register();

    await PushNotifications.addListener('registration', (token: Token) => {
      // Store Generated FCM token in redux store
      this.store$.dispatch(new CoreActionSetFCMToken(token.value));
    });
  };

  /**
   * Register Push Notification event listener.
   */
  registerPushNotificationListener = async () => {
    // For Remote Push Notification
    await PushNotifications.addListener(
      'pushNotificationReceived',
      async notification => {
        await Haptics.impact({ style: ImpactStyle.Medium });
        this.inAppToast({
          title: notification.title,
          content: notification.body
        });
      }
    );

    // For Remote Push Notification on tap
    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      notification => {
        if (
          notification &&
          notification.actionId &&
          notification.actionId === 'tap'
        )
          this.setNotificationTap();
      }
    );

    // For Local Push Notification
    await LocalNotifications.addListener(
      'localNotificationReceived',
      async notification => {
        await Haptics.impact({ style: ImpactStyle.Medium });
        if (notification && notification.id) {
          this.inAppToast({
            title: notification.title,
            content: notification.body,
            isReminder: notification.extra?.type === 'reminder' ? true : false
          });
        }
      }
    );

    // For Local Push Notification on tap
    await LocalNotifications.addListener(
      'localNotificationActionPerformed',
      notification => {
        if (
          notification &&
          notification.actionId &&
          notification.actionId === 'tap'
        ) {
          this.setNotificationTap();
        }
      }
    );
  };

  setNotificationTap(): void {
    if (this.router.url.includes('timeline')) {
      this.store$.dispatch(new CoreActionSetNotificationTap(true));
    }
  }

  // InApp Notification using ToastController
  inAppToast({ title, content, isReminder = false }): void {
    this.toastCtrl
      .create({
        header: title,
        message: content,
        animated: true,
        position: 'top',
        mode: 'ios',
        cssClass: 'bepatient_dynamic_inApp_notification',
        duration: 10000, // Auto-dismiss after 10 seconds
        buttons: [
          {
            icon: 'close',
            role: 'cancel'
          }
        ]
      })
      .then(async toast => {
        await toast.present();
      })
      .finally(() => {
        if (!isReminder) {
          this.setNotificationTap();
        }
      });
  }

  /**
   * To remove existing biometric data for user using APP with capacitor for the first time.
   */
  userPreferenceCheck = async () => {
    const { value } = await Preferences.get({ key: 'smartApp' });
    if (!value) {
      this.biometric.clearDeviceBiometricData();
    }
    Preferences.set({
      key: 'smartApp',
      value: this.env.version
    });
  };

  initializeDeeplinkListener() {
    // iOS and Android supported
    // App cold start and when app is in background
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      if (event.url && event.url.includes('com.bepatient')) {
        // For external app callbacks, close browser and process immediately
        // Extract target from callback URL for validation
        const callbackMatch = event.url.match(
          /com\.bepatient\.smartapp:\/\/([^-]+)-callback/
        );
        if (callbackMatch) {
          const callbackTarget = callbackMatch[1];
          console.log(
            'Deeplink data received - external app callback for target:',
            callbackTarget
          );
          // Close browser immediately when external app callback is received
          this.closeExternalBrowser()
            .then(() => {
              console.log('External browser closed after callback');
            })
            .catch(error => {
              console.warn('Browser close failed or not needed:', error);
            });

          // Mark that we received a real deeplink callback
          localStorage.setItem('external-app-deeplink-received', 'true');
          localStorage.setItem(
            'external-app-deeplink-timestamp',
            new Date().toISOString()
          );

          // Get the original target that was stored when assessment was opened
          const originalTarget = localStorage.getItem(
            'external-app-original-target'
          );

          // Process the callback immediately with proper target validation
          this.ngZone.run(() => {
            this.deeplinkHandler(event.url, originalTarget);
          });
        } else {
          // For other deeplinks, process immediately
          this.ngZone.run(() => {
            this.deeplinkHandler(event.url);
          });
        }
      }
    });
  }

  // MYFSHD: Deeplink handler
  private deeplinkHandler(launchUrl: string, expectedTarget?: string): void {
    const url = launchUrl;
    // Parse url to get the query params
    const urlObj = new URL(url);
    const params = urlObj.searchParams;
    const success = params.get('success');

    // Handle external app callback with strict target validation
    const callbackMatch = url.match(
      /com\.bepatient\.smartapp:\/\/([^-]+)-callback/
    );
    if (callbackMatch) {
      const callbackTarget = callbackMatch[1];

      // Validate that the callback target matches the original target that was opened
      if (expectedTarget && callbackTarget !== expectedTarget) {
        console.error(
          `Callback target mismatch: expected '${expectedTarget}', received '${callbackTarget}'`
        );
        return;
      }

      // Validate that the callback target is in the allowed list
      if (!this.coreExternalAppService.isTargetAllowed(callbackTarget)) {
        console.error(`Callback target '${callbackTarget}' is not allowed`);
        return;
      }

      console.log(
        'External app assessment completed - processing callback for target:',
        callbackTarget
      );

      // Clean up the stored target after successful validation
      localStorage.removeItem('external-app-original-target');

      this.handleExternalAppCallback(url, params);
      return;
    }

    if (!success) {
      return;
    }

    const popupData = {
      title:
        success === 'true'
          ? this.translate.instant('DEEPLINK_ACTIVITY_POPUP.TITLE_SUCCESS')
          : this.translate.instant('DEEPLINK_ACTIVITY_POPUP.TITLE_FAILURE'),
      message:
        success === 'true'
          ? this.translate.instant('DEEPLINK_ACTIVITY_POPUP.MESSAGE_SUCCESS')
          : this.translate.instant('DEEPLINK_ACTIVITY_POPUP.MESSAGE_FAILURE'),
      buttons: [this.translate.instant('DEEPLINK_ACTIVITY_POPUP.BUTTON.OK')]
    };

    // If form page is active show the popup
    if (success && this.router.url.includes('my-form')) {
      // Save form detail received in deeplink
      const fieldId = params.get('fieldId');
      const formId = params.get('activityId');
      const fileName = params.get('fileName');

      setTimeout(() => {
        this.sharedPopupService.simpleAlert({
          title: popupData.title,
          message: popupData.message,
          buttons: popupData.buttons,
          id: 'deeplink'
        });
      }, 300);

      if (fieldId && formId && fileName) {
        this.store$.dispatch(
          new CoreActionSaveDeeplinkFormData({
            formId,
            fieldId,
            fileName
          })
        );
      }
    } else {
      // If form page is not active show the toast
      this.toastCtrl
        .create({
          header: popupData.title,
          message: popupData.message,
          animated: true,
          position: 'top',
          duration: 4000,
          cssClass: 'bepatient_dynamic_form_save_toast'
        })
        .then(async toast => {
          await toast.present();
        });
    }
  }

  /**
   * Extract user ID from JWT token
   * @param token JWT token string
   * @returns User ID if found, null otherwise
   */
  extractUserIdFromToken(token: string): number | null {
    try {
      if (!token) {
        return null;
      }

      // JWT tokens have 3 parts separated by dots: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.warn('Invalid JWT token format');
        return null;
      }

      // Decode the payload (second part)
      const payload = parts[1];

      // Add padding if needed for base64 decoding
      const paddedPayload =
        payload + '='.repeat((4 - (payload.length % 4)) % 4);

      // Decode base64
      const decodedPayload = atob(paddedPayload);

      // Parse JSON
      const payloadData = JSON.parse(decodedPayload);

      // Extract uid from token payload
      if (payloadData.uid) {
        const userId = parseInt(payloadData.uid, 10);
        return userId;
      }

      console.warn('No uid found in JWT token payload');
      return null;
    } catch (error) {
      console.error('Error extracting user ID from JWT token:', error);
      return null;
    }
  }

  /**
   * Build dynamic URL for external app assessments
   * @param originalUrl The original URL from the form
   * @param loginData User login data containing JWT token
   * @param formData Form data containing form ID and occurrence
   * @param environment Environment configuration
   * @param isMobileDevice Whether the device is mobile
   * @returns Promise with the dynamically built URL
   */
  async buildDynamicExternalAppUrl(
    originalUrl: string,
    loginData: any,
    formData: any,
    environment: any,
    isMobileDevice: boolean = false,
    target: string = 'pvt'
  ): Promise<string> {
    try {
      console.log(
        'Building dynamic external app URL with isMobileDevice:',
        isMobileDevice
      );

      if (!loginData || !loginData.iAuth) {
        console.warn('No login data available for external app URL building');
        return originalUrl;
      }

      // Extract user ID from JWT token
      const userId = this.extractUserIdFromToken(loginData.iAuth);
      if (userId === null) {
        console.warn('No user ID available in token');
        return originalUrl;
      }

      // Build dynamic user ID: env_instanceCode_userId_formId_occurrence
      const dynamicUserId = this.buildDynamicUserIdSync(
        formData,
        userId,
        environment
      );
      // Replace or append userid parameter
      let urlWithUserId = originalUrl;
      if (originalUrl.includes('userid=')) {
        // Replace existing userid parameter
        urlWithUserId = originalUrl.replace(
          /userid=[^&]*/,
          `userid=${dynamicUserId}`
        );
      } else {
        // Append new userid parameter
        const separator = originalUrl.includes('?') ? '&' : '?';
        urlWithUserId = `${originalUrl}${separator}userid=${dynamicUserId}`;
      }

      // Add callback URL for mobile devices
      if (isMobileDevice) {
        const callbackUrl = `com.bepatient.smartapp://${target}-callback`;
        urlWithUserId += `&callback_url=${encodeURIComponent(callbackUrl)}`;

        // Store the original target for validation when callback is received
        localStorage.setItem('external-app-original-target', target);
        console.log('Original target stored for validation:', target);
      }
      return urlWithUserId;
    } catch (error) {
      console.error('Error building dynamic external app URL:', error);
      return originalUrl; // Return original URL if error occurs
    }
  }

  /**
   * Build dynamic user ID with format: env_instanceCode_userId_formId_occurrence
   * @param formData Form data containing formId and occurrence
   * @param userId User ID (extracted from JWT token)
   * @param environment Environment configuration
   * @returns Dynamic user ID string
   */
  buildDynamicUserIdSync(
    formData: any,
    userId: number,
    environment: any
  ): string {
    // Get environment (dev, beta, prod)
    const env = this.getEnvironment(environment);

    // Get instance code
    const instanceCode = this.getInstanceCode(environment);

    // Get form ID
    const formId = this.getFormId(formData);

    // Get occurrence number
    const occurrence = this.getOccurrenceNumber(formData);

    // Build dynamic user ID
    let dynamicUserId = '';

    if (env === 'prod') {
      // Production: instanceCode_userId_formId_occurrence
      dynamicUserId = `${instanceCode}_${userId}_${formId}_${occurrence}`;
    } else {
      // Development/Beta: env_instanceCode_userId_formId_occurrence
      dynamicUserId = `${env}_${instanceCode}_${userId}_${formId}_${occurrence}`;
    }

    return dynamicUserId;
  }

  /**
   * Get environment type (dev, beta, prod)
   * @param environment Environment configuration
   */
  private getEnvironment(environment: any): string {
    // Check if we're in production
    if (environment?.production) {
      return 'prod';
    }

    // Check devType for beta
    if (environment?.devType === 'BETA') {
      return 'beta';
    }

    // Default to dev
    return 'dev';
  }

  /**
   * Get instance code from localStorage
   * @param environment Environment configuration
   */
  private getInstanceCode(environment: any): string {
    try {
      // Get from localStorage core.instancesData.dev.code
      const coreState = localStorage.getItem('core');
      if (coreState) {
        const parsed = JSON.parse(coreState);
        const devTypeKey = environment?.devType?.toLowerCase() || 'dev';
        if (
          parsed.instancesData &&
          parsed.instancesData[devTypeKey] &&
          parsed.instancesData[devTypeKey].code
        ) {
          return parsed.instancesData[devTypeKey].code;
        }
      }
    } catch (error) {
      console.error('Error getting instance code from localStorage:', error);
    }

    return 'unknown';
  }

  /**
   * Get form ID from form data
   * @param formData Form data
   */
  private getFormId(formData: any): string {
    if (formData) {
      // Try formId first, then uuid as fallback
      if (formData.formId) {
        return formData.formId;
      }
      if (formData.uuid) {
        return formData.uuid;
      }
    }

    // Default to unknown if no form data
    return 'unknown';
  }

  /**
   * Get occurrence number from form data
   * @param formData Form data
   */
  private getOccurrenceNumber(formData: any): string {
    if (formData) {
      // Try intPageOccurrence first, then occurrence as fallback
      if (
        formData.intPageOccurrence !== undefined &&
        formData.intPageOccurrence !== null
      ) {
        return formData.intPageOccurrence.toString();
      }
      if (formData.occurrence !== undefined && formData.occurrence !== null) {
        return formData.occurrence.toString();
      }
    }

    // Default to 0 if no occurrence found
    return '0';
  }

  /**
   * Handle external app callback from deeplink
   */
  private handleExternalAppCallback(
    url: string,
    params: URLSearchParams
  ): void {
    try {
      // Check if this is a real deeplink callback (not manual browser close)
      const deeplinkReceived = localStorage.getItem(
        'external-app-deeplink-received'
      );
      const deeplinkTimestamp = localStorage.getItem(
        'external-app-deeplink-timestamp'
      );

      if (!deeplinkReceived || deeplinkReceived !== 'true') {
        return;
      }

      // Check if deeplink was received recently (within last 30 seconds)
      if (deeplinkTimestamp) {
        const deeplinkTime = new Date(deeplinkTimestamp).getTime();
        const currentTime = new Date().getTime();
        const timeDiff = currentTime - deeplinkTime;

        if (timeDiff > 30000) {
          // 30 seconds
          return;
        }
      }

      // Get actual status from callback (use what third-party sends)
      const status = params.get('status') || 'completed';
      const error = params.get('error') || params.get('message') || '';
      const userId = params.get('userId') || '';

      // Get pending assessments to process
      const pendingAssessments =
        this.coreExternalAppService.getPendingExternalAppAssessments();
      const uniqueKeys = Object.keys(pendingAssessments);

      if (uniqueKeys.length === 0) {
        return;
      }

      // Process each pending assessment with actual callback data
      uniqueKeys.forEach(uniqueKey => {
        // Determine if assessment was actually completed based on status
        const isCompleted = status === 'completed';

        // Store actual callback data in localStorage
        const callbackData = {
          completed: isCompleted,
          status: status,
          error: error,
          userId: userId,
          completedAt: new Date().toISOString(),
          method: 'deeplink',
          uniqueKey: uniqueKey,
          callbackData: {
            url: url,
            params: this.urlSearchParamsToObject(params)
          }
        };

        // Store in deeplink-form-completions
        const existingData = this.getDeeplinkFormCompletionData();
        existingData[uniqueKey] = callbackData;
        localStorage.setItem(
          'deeplink-form-completions',
          JSON.stringify(existingData)
        );

        // Dispatch action to trigger button state updates
        this.store$.dispatch(
          new CoreActionSaveDeeplinkFormData({
            uniqueKey: uniqueKey,
            completionData: callbackData
          })
        );

        // Auto-submit form for this occurrence
        this.autoSubmitFormForOccurrence(uniqueKey);

        // Remove from pending assessments
        this.coreExternalAppService.removePendingExternalAppAssessment(
          uniqueKey
        );
      });

      // Clear the deeplink flag after processing
      localStorage.removeItem('external-app-deeplink-received');
      localStorage.removeItem('external-app-deeplink-timestamp');
    } catch (error) {
      console.error('Error handling external app callback:', error);
    }
  }

  /**
   * Get deeplink form completion data from localStorage
   */
  private getDeeplinkFormCompletionData(): any {
    try {
      const data = localStorage.getItem('deeplink-form-completions');
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error('Error getting deeplink form completion data:', error);
      return {};
    }
  }

  /**
   * Close external browser when external app callback is received
   */
  private async closeExternalBrowser(): Promise<void> {
    try {
      // Check if we're on a native platform
      if (!Capacitor.isNativePlatform()) {
        return;
      }

      // Try to close the browser
      const { Browser } = await import('@capacitor/browser');
      await Browser.close();
    } catch (error) {
      console.log('Could not close external browser:', error);
      throw error;
    }
  }

  /**
   * Auto-submit form when external app assessment is completed
   */
  private autoSubmitFormForOccurrence(uniqueKey: string): void {
    try {
      // Dispatch SAVE_CURRENT_PAGE to follow the normal form submission flow
      // This will trigger the same API call and response handling as manual submission
      // TODO-CIRCULAR-DEPENDENCY
      // this.store$.dispatch(new MyFormActionSaveCurrentPage()); // Commented out to avoid circular dependency
      this.store$.dispatch({ type: '[MyFormAction] Save current page' }); // Using string-based action instead
    } catch (error) {
      console.error('Error auto-submitting form:', error);
    }
  }

  /**
   * Convert URLSearchParams to a plain object (compatible with older TypeScript targets)
   */
  private urlSearchParamsToObject(params: URLSearchParams): any {
    const obj: any = {};
    params.forEach((value, key) => {
      obj[key] = value;
    });
    return obj;
  }

  // Encode text to Base64 (handles Unicode)
  textToBase64(text) {
    return btoa(
      new TextEncoder()
        .encode(text)
        .reduce((data, byte) => data + String.fromCharCode(byte), '')
    );
  }

  // Decode Base64 to text (handles Unicode)
  base64ToText(base64) {
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  }

  /**
   * Returns a lightness-adjusted version of hex so the brand color visually
   * suits the current theme background. Hue and saturation are preserved.
   *
   * Delegates to the canonical WCAG-AA utility in @bepatient/shared — see
   * docs/adr/0001-contrast-utility-consolidation.md.
   */
  getThemeAwareColor(hex: string, isFactsheet = false): string {
    return canonicalGetThemeAwareColor(hex, isFactsheet);
  }

  /**
   * User-authored HTML (e.g. question titles, factsheet content from the API)
   * can carry inline `color` styles or the legacy `color="…"` HTML attribute
   * (e.g. `<font color="#05351F">`) that become unreadable against the current
   * theme background (light / dark / high-contrast). Walk every styled element
   * and run each color through getThemeAwareColor so the rendered text keeps
   * sufficient contrast regardless of theme.
   */
  adjustInlineColorsForTheme(html: string, isFactsheet = false): string {
    if (!html || typeof html !== 'string') {
      return html;
    }
    if (!html.toLowerCase().includes('color')) {
      return html;
    }
    try {
      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;

      // Pass 1: inline `style` colors (modern markup).
      const styled = wrapper.querySelectorAll<HTMLElement>('[style]');
      styled.forEach(el => {
        const inlineBg = el.style.backgroundColor;
        const inlineColor = el.style.color;

        // Element carries its own background-color: theme-adjust the bg so
        // it stays visible against the page background, then derive a text
        // color whose contrast is computed against the *new* bg (not the
        // page bg). Author intent (colored chip / highlight) is preserved
        // while accessibility is enforced.
        if (inlineBg) {
          const parsedBg = this.parseCssColor(inlineBg);
          if (parsedBg) {
            const adjustedBg = this.getThemeAwareColor(
              parsedBg.hex(),
              isFactsheet
            );
            if (adjustedBg) {
              el.style.backgroundColor = adjustedBg;
              el.style.color = this.getAccessibleTextColor(adjustedBg);
            }
          }
          return;
        }

        // No local background: text contrast is against the page background,
        // so theme-adjust the foreground color directly.
        if (!inlineColor) {
          return;
        }
        const parsed = this.parseCssColor(inlineColor);
        if (!parsed) {
          return;
        }
        const adjusted = this.getThemeAwareColor(parsed.hex(), isFactsheet);
        if (adjusted) {
          el.style.color = adjusted;
        }
      });

      // Pass 2: legacy `color` HTML attribute (e.g. `<font color="#05351F">`).
      // Skipped if the same element already had its inline style.color
      // adjusted in pass 1, so we don't double-process.
      const colorAttrEls = wrapper.querySelectorAll<HTMLElement>('[color]');
      colorAttrEls.forEach(el => {
        if (el.style && el.style.color) {
          return;
        }
        const colorAttr = el.getAttribute('color');
        if (!colorAttr) {
          return;
        }
        const parsed = this.parseCssColor(colorAttr);
        if (!parsed) {
          return;
        }
        const adjusted = this.getThemeAwareColor(parsed.hex(), isFactsheet);
        if (adjusted) {
          el.setAttribute('color', adjusted);
        }
      });

      return wrapper.innerHTML;
    } catch (e) {
      return html;
    }
  }

  /**
   * Same intent as adjustInlineColorsForTheme, but for a plain CSS object as
   * consumed by Angular's [ngStyle]. Accepts either an object or a JSON
   * string and returns a new object with `color` / `background-color`
   * theme-adjusted; all other keys pass through untouched.
   *
   *   - If the object carries a non-empty `background-color`, the bg is
   *     theme-adjusted against the page bg and the matching `color` is
   *     re-derived from the new bg via getAccessibleTextColor.
   *   - Otherwise, a non-empty `color` is theme-adjusted directly.
   *
   * Both kebab-case (`background-color`) and camelCase (`backgroundColor`)
   * keys are supported, and the original key style is preserved on output.
   */
  adjustCssObjectColorsForTheme(css: any, isFactsheet = false): any {
    if (!css) {
      return css;
    }
    try {
      let source: any = css;
      if (typeof source === 'string') {
        try {
          source = JSON.parse(source);
        } catch (e) {
          return css;
        }
      }
      if (typeof source !== 'object') {
        return css;
      }

      const result = { ...source };
      const bgKey =
        'background-color' in result
          ? 'background-color'
          : 'backgroundColor' in result
          ? 'backgroundColor'
          : null;
      const colorKey =
        'color' in result ? 'color' : 'Color' in result ? 'Color' : null;

      if (bgKey && result[bgKey]) {
        const parsedBg = this.parseCssColor(result[bgKey]);
        if (parsedBg) {
          const adjustedBg = this.getThemeAwareColor(
            parsedBg.hex(),
            isFactsheet
          );
          if (adjustedBg) {
            result[bgKey] = adjustedBg;
            const textKey = colorKey || 'color';
            result[textKey] = this.getAccessibleTextColor(adjustedBg);
          }
        }
        return result;
      }

      if (colorKey && result[colorKey]) {
        const parsed = this.parseCssColor(result[colorKey]);
        if (parsed) {
          const adjusted = this.getThemeAwareColor(parsed.hex(), isFactsheet);
          if (adjusted) {
            result[colorKey] = adjusted;
          }
        }
      }

      return result;
    } catch (e) {
      return css;
    }
  }

  /**
   * Parse any CSS color value into a chroma instance.
   * `el.style.color` is normalised by the browser to rgb()/rgba(), but author
   * markup may also use hex, hsl(), or named colors. chroma's single-arg
   * constructor only handles hex / short-hex / named colors, so rgb()/hsl()
   * strings have to be parsed and fed through the component-form constructors.
   * Returns null for empty / `transparent` / `currentColor` / `inherit`, or
   * anything chroma can't make sense of.
   */
  parseCssColor(value: string): chroma.Color | null {
    if (!value) {
      return null;
    }
    const v = value.trim().toLowerCase();
    if (!v || v === 'transparent' || v === 'inherit' || v === 'currentcolor') {
      return null;
    }

    // rgb(r, g, b) / rgba(r, g, b, a) — supports comma OR space separators and
    // the modern `rgb(r g b / a)` slash-alpha syntax.
    const rgbMatch = v.match(
      /^rgba?\(\s*(\d+(?:\.\d+)?)\s*[,\s]\s*(\d+(?:\.\d+)?)\s*[,\s]\s*(\d+(?:\.\d+)?)(?:\s*[,/]\s*(\d*\.?\d+%?))?\s*\)$/
    );
    if (rgbMatch) {
      const r = parseFloat(rgbMatch[1]);
      const g = parseFloat(rgbMatch[2]);
      const b = parseFloat(rgbMatch[3]);
      let c = chroma(r, g, b);
      if (rgbMatch[4] !== undefined) {
        c = c.alpha(this.parseAlpha(rgbMatch[4]));
      }
      return c;
    }

    // hsl(h, s%, l%) / hsla(...). h may carry a `deg` suffix.
    const hslMatch = v.match(
      /^hsla?\(\s*(\d*\.?\d+)(?:deg)?\s*[,\s]\s*(\d*\.?\d+)%\s*[,\s]\s*(\d*\.?\d+)%(?:\s*[,/]\s*(\d*\.?\d+%?))?\s*\)$/
    );
    if (hslMatch) {
      const h = parseFloat(hslMatch[1]);
      const s = parseFloat(hslMatch[2]) / 100;
      const l = parseFloat(hslMatch[3]) / 100;
      let c = chroma.hsl(h, s, l);
      if (hslMatch[4] !== undefined) {
        c = c.alpha(this.parseAlpha(hslMatch[4]));
      }
      return c;
    }

    // Hex (`#ff3399`, `#F39`) or CSS named colors (`red`, `rebeccapurple`, …).
    try {
      return chroma(v);
    } catch (e) {
      return null;
    }
  }

  private parseAlpha(token: string): number {
    return token.endsWith('%') ? parseFloat(token) / 100 : parseFloat(token);
  }

  /**
   * Applies the accent Ionic color variables to the document root, adjusted for
   * contrast against the current theme background. Returns the theme-aware
   * color it derived so callers can reuse it as the single source of truth
   * (avoids re-deriving via a second getThemeAwareColor call).
   */
  applyIonicColorToRoot(name: string, hex: string): string {
    // Adjust accent color to meet contrast against the current theme background (all themes).
    const effectiveHex = this.getThemeAwareColor(hex);
    const base = chroma(effectiveHex);

    const contrast =
      chroma.contrast(base, '#000000') >= chroma.contrast(base, '#ffffff')
        ? '#000000'
        : '#ffffff';

    const set = (key: string, value: string) =>
      document.documentElement.style.setProperty(key, value);

    set(`--ion-color-${name}`, base.hex());
    set(`--ion-color-${name}-rgb`, base.rgb().join(','));
    set(`--ion-color-${name}-contrast`, contrast);
    set(`--ion-color-${name}-contrast-rgb`, chroma(contrast).rgb().join(','));
    set(`--ion-color-${name}-shade`, base.mix('#000', 0.12).hex());
    set(`--ion-color-${name}-tint`, base.mix('#fff', 0.1).hex());

    // Accessible button vars used in Dark + High Contrast: my-account-toolbar, my-account-icon (--ion-color-*-accessible-button, -text, -alpha).
    set(`--ion-color-${name}-accessible-button`, base.hex());
    set(
      `--ion-color-${name}-accessible-button-alpha`,
      chroma(base).alpha(0.5).hex()
    );
    set(
      `--ion-color-${name}-accessible-button-text`,
      this.getAccessibleTextColor(base.hex())
    );

    return effectiveHex;
  }

  /**
   * Get an accessible button color based on background and minimum contrast.
   *
   * Delegates to the canonical WCAG-AA utility in @bepatient/shared — see
   * docs/adr/0001-contrast-utility-consolidation.md.
   */
  getAccessibleButtonColor(
    buttonHex: string,
    backgroundHex = '#000000', // Dark Mode + High Contrast: background is always black when used from applyIonicColorToRoot.
    minContrast = 4.5
  ): string {
    return canonicalGetAccessibleButtonColor(
      buttonHex,
      backgroundHex,
      minContrast
    );
  }

  /**
   * Given a background color, return a foreground text color that meets WCAG AA
   * contrast against it.
   *
   * Delegates to the canonical WCAG-AA utility in @bepatient/shared — see
   * docs/adr/0001-contrast-utility-consolidation.md.
   */
  getAccessibleTextColor(backgroundHex: string, minContrast = 4.5): string {
    return canonicalGetAccessibleTextColor(backgroundHex, minContrast);
  }
}
